/**
 * 비용 시뮬레이터(/cost-simulator) 설명 문단 + FAQ 데이터.
 *
 * 영구지침: docs/develop/domain/cost/비용_시뮬레이터_구현_지침.md §7.3,
 *          docs/develop/seo/structured-data-spec.md §5.2
 *
 * 화면 문구와 FAQPage 구조화 데이터를 **이 배열 하나**에서 만든다. 답변은 조각 배열로 적는다 —
 * 문자열은 그대로, `{ text, to }` 는 화면에서 내부 링크(<Link>)가 되고 JSON-LD 에서는 text 만 남는다.
 * 평문을 따로 적지 않으므로 화면과 구조화 데이터가 어긋날 수 없다.
 *
 * 규칙
 * - 원 단위 금액·수가·한도액을 쓰지 않는다. 해마다 바뀌어서 하드코딩하면 반드시 틀어진다.
 *   제도가 바뀌어야 움직이는 비율(20%·15%·60% 감경 등)만 쓰고 금액은 계산기로 보낸다.
 *   (costSimulatorFaqs.test.ts 가 "숫자+원" 패턴 부재를 확인한다)
 * - /care-calculator FAQ(CareCalcFaq.tsx)와 같은 질문 문장을 쓰지 않는다 — 검색어 경쟁 방지.
 * - 연말정산·본인부담상한제는 넣지 않는다(사실 미확인). 요양병원 간병비 급여화는 "예정"으로만.
 */

export type FaqSegment = string | { text: string; to: string }

export interface CostSimulatorFaqItem {
  question: string
  segments: readonly FaqSegment[]
}

/** 조각 배열 → 평문 (JSON-LD answer.text, 테스트용) */
export function segmentsToText(segments: readonly FaqSegment[]): string {
  return segments.map((s) => (typeof s === 'string' ? s : s.text)).join('')
}

/** FAQPageJsonLd 에 넘길 { question, answer } 배열 */
export function toFaqJsonLdItems(
  faqs: readonly CostSimulatorFaqItem[],
): { question: string; answer: string }[] {
  return faqs.map((f) => ({ question: f.question, answer: segmentsToText(f.segments) }))
}

const GUIDE_COST = '/guide/cost'
const CARE_CALCULATOR = '/care-calculator'

/** 설명 문단 제목 */
export const COST_SIMULATOR_INTRO_TITLE = '요양원 비용은 이렇게 계산해요'

/** 설명 문단 — 기준 표기("2026년 1월 1일 고시 기준")는 여기 한 번만 쓴다 */
export const COST_SIMULATOR_INTRO: readonly (readonly FaqSegment[])[] = [
  [
    '이 계산기는 장기요양등급과 이용할 서비스를 고르면 한 달에 직접 내는 돈을 계산해요. ',
    '요양원, 주간보호, 방문요양을 계산할 수 있고, 요양병원은 비용 구조가 달라 계산하지 않아요. ',
    '수가와 월 한도액은 2026년 1월 1일 고시 기준이에요.',
  ],
  [
    '계산식은 "급여비용 × 본인부담률 + 비급여"예요. ',
    '급여비용은 하루(방문요양은 1회) 수가에 이용 일수를 곱한 금액이에요. ',
    '본인부담률은 요양원 20%, 방문요양·주간보호 15%이고, 감경 대상이면 더 낮아져요.',
  ],
  [
    '비급여 기본값은 식재료비나 간식비 같은 항목의 전국 평균으로 잡은 추정치예요. ',
    '시설 상세 페이지에서 비용 계산으로 들어오면, 그 시설이 신고한 비급여 금액이 있을 때 그 금액으로 계산해요. ',
    '비용 구조를 더 자세히 보고 싶으시면 ',
    { text: '요양 비용 가이드', to: GUIDE_COST },
    '를 참고해 주세요.',
  ],
]

/** FAQ 제목 */
export const COST_SIMULATOR_FAQ_TITLE = '요양원 비용, 자주 묻는 질문'

export const COST_SIMULATOR_FAQS: readonly CostSimulatorFaqItem[] = [
  {
    question: '요양원 한 달 비용은 어떻게 계산하나요?',
    segments: [
      '본인부담금에 비급여를 더하면 돼요. ',
      '본인부담금은 등급별 하루 수가에 입소 일수를 곱한 급여비용 가운데 20%예요. ',
      '여기에 식재료비, 이미용비처럼 보험이 적용되지 않는 비급여가 붙어요. ',
      '정확한 금액은 위 계산기에서 등급과 시설 규모를 골라 확인해 보세요.',
    ],
  },
  {
    question: '등급별로 본인부담금이 달라지나요?',
    segments: [
      '본인부담률은 등급과 상관없이 요양원 20%로 같아요. 달라지는 건 하루 수가예요. ',
      '1등급이 가장 높고 2등급이 그다음이며, 3·4·5등급은 수가가 같아서 본인부담금도 같아요. ',
      '방문요양·주간보호는 등급마다 월 한도액이 달라서 쓸 수 있는 서비스 양이 달라져요.',
    ],
  },
  {
    question: '본인부담금 감경 대상은 누구이고 얼마나 줄어드나요?',
    segments: [
      '건강보험료 순위와 의료급여 여부로 나뉘어요. ',
      '기초생활수급자가 아닌 의료급여 수급자(국가유공자·북한이탈주민 등), 병원비 경감을 받는 차상위계층, ',
      '재난 등으로 생계가 어려운 분, 건강보험료 하위 25% 이하는 60% 감경이에요. 요양원은 8%, 방문요양·주간보호는 6%만 내요. ',
      '건강보험료 하위 25% 초과~50% 이하는 40% 감경이라 요양원 12%, 방문요양·주간보호 9%예요. ',
      '의료급여를 받는 기초생활수급자는 본인부담금이 없어요. 직장가입자는 재산 기준도 함께 충족해야 감경돼요.',
    ],
  },
  {
    question: '기초생활수급자는 요양원 비용이 무료인가요?',
    segments: [
      '장기요양 본인부담금은 없지만 비급여는 내야 해요. ',
      '의료급여를 받는 기초생활수급자는 장기요양 본인부담률이 0%예요. ',
      '하지만 식재료비, 상급침실료, 이미용비, 일상생활비 같은 비급여는 수급자도 전액 본인이 부담해요. ',
      '계산기에서 "본인부담 면제"를 고르면 비급여만 남은 금액을 볼 수 있어요.',
    ],
  },
  {
    question: '요양원 식비도 장기요양보험이 적용되나요?',
    segments: [
      '적용되지 않아요. 요양원 식재료비는 비급여라 전액 본인이 내고, 감경 대상자나 의료급여 수급자도 마찬가지예요. ',
      '금액은 시설마다 달라요. 계산기는 기본으로 전국 평균 추정치를 넣고, ',
      '시설을 정해서 들어오면 그 시설이 신고한 금액을 써요.',
    ],
  },
  {
    question: '요양원에서 1인실을 쓰면 비용이 더 드나요?',
    segments: [
      '네, 더 들어요. 1인실처럼 상급침실을 쓰면 상급침실료가 따로 붙어요. ',
      '상급침실료는 비급여라 장기요양보험이 적용되지 않고, 감경 대상자도 전액 내요. ',
      '시설마다 금액 차이가 커서 입소 상담 때 방 종류별 금액을 물어보시는 게 좋아요.',
    ],
  },
  {
    question: '감경은 따로 신청해야 하나요?',
    segments: [
      '대부분은 신청하지 않아도 돼요. 공단이 직권으로 적용해요. ',
      '건강보험료가 바뀌어 새로 감경 대상이 됐다면 공단 운영센터에 신청할 수 있고, 결과는 14일 이내에 통보돼요. ',
      '지금 감경을 받고 있는지는 국민건강보험공단(1577-1000)에 문의하면 알 수 있어요.',
    ],
  },
  {
    question: '요양원과 요양병원 비용은 어떻게 다른가요?',
    segments: [
      '적용되는 보험이 달라요. 요양원은 장기요양보험의 시설급여라 장기요양등급이 있어야 하고, 본인부담률 20%가 적용돼요. ',
      '요양병원은 건강보험이 적용되는 의료기관이라 진료비를 계산하는 방식이 달라요. ',
      '요양병원 간병비는 지금은 전액 본인부담이고, 2027년 상반기에 시범 급여화가 예정돼 있어요. ',
      '이 계산기는 요양원·주간보호·방문요양만 계산해요.',
    ],
  },
  {
    question: '3·4·5등급도 요양원에 입소할 수 있나요?',
    segments: [
      '등급판정위원회의 인정을 받으면 입소할 수 있어요. ',
      '요양원은 1·2등급이 원칙이고, 3~5등급은 가족이 돌보기 어려운 사정 등이 있을 때 위원회가 시설급여가 필요하다고 인정해야 해요. ',
      '아직 등급을 모르신다면 ',
      { text: '요양등급 계산기', to: CARE_CALCULATOR },
      '로 예상 등급부터 확인해 보세요.',
    ],
  },
  {
    question: '방문요양·주간보호는 월 한도액을 넘기면 어떻게 되나요?',
    segments: [
      '한도를 넘긴 금액은 전액 본인이 내요. ',
      '방문요양·주간보호 같은 재가급여는 등급마다 한 달에 쓸 수 있는 한도액이 정해져 있어요. ',
      '한도 안에서는 15%(감경 대상은 더 낮게)만 내지만, 넘긴 금액에는 보험이 적용되지 않아요. 요양원은 월 한도가 없어요. ',
      '등급별 한도액은 ',
      { text: '요양 비용 가이드', to: GUIDE_COST },
      '에 정리해 두었고, 위 계산기에서 방문요양이나 주간보호를 고르면 한도 초과분을 따로 보여드려요.',
    ],
  },
  {
    question: '같은 등급인데 요양원마다 비용이 다른 이유는 뭔가요?',
    segments: [
      '장기요양 수가는 전국이 같지만 두 군데서 차이가 나요. ',
      '먼저 시설 규모예요. 정원 10명 이상 요양원과 10명 미만 소규모 시설은 하루 수가가 달라요. ',
      '다음은 비급여예요. 식재료비나 상급침실료 같은 비급여는 시설이 정해서 신고하기 때문에 차이가 커요. ',
      '시설 상세 페이지에서 비용 계산으로 들어오면 그 시설이 신고한 비급여로 계산해 볼 수 있어요.',
    ],
  },
]
